/**
 * Production accordion body — well list (prod/inj groups) + linked Voronoi charts.
 * Link rule matches handoff wellData.js: linked iff !blocked && w > 0 across all grids.
 */
import { PlayArrow as PlayIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

// import { MaterialBalanceOptimizeDialog } from '@/components/Scenarios/MaterialBalanceOptimizeDialog';
import { FieldProductionChart } from '@/components/Scenarios/FieldProductionChart';
import { MaterialBalancePlayDialog } from '@/components/Scenarios/MaterialBalancePlayDialog';
import type { MbWellRow } from '@/components/Scenarios/MaterialBalanceWellsSection';
import type { MaterialBalanceResult, MbPoint } from '@/types/materialBalance';
import type { MbOptimizeProposal } from '@/utils/materialBalanceOptimize';
import {
  injSeries,
  linkedInjs,
  linkedProdRateSeries,
  linkedProds,
  mbEngineWellId,
  prodSeries,
} from '@/utils/materialBalanceWellData';
import { computeLayerExtractable } from '@/utils/scenarioLayerNiz';

type InjPt = { year: number; value: number };

export interface MaterialBalanceProductionPanelProps {
  scenarioId: string;
  layerId: string;
  wells: MbWellRow[];
  result: MaterialBalanceResult | null;
  /** Contour in local km (same CRS as Voronoi grid) */
  contour?: MbPoint[];
  /** Geographic origin for basemap projection */
  contourOrigin?: { lat: number; lng: number } | null;
  so0?: number;
  layerName?: string;
  injWells?: Record<string, Record<string, number>>;
  selectedWellId?: string | null;
  onSelectWell?: (id: string) => void;
  onRun?: () => void | Promise<void>;
  /** Immediate local update of per-well injection overrides */
  onInjWellsChange?: (injWells: Record<string, Record<string, number>>) => void;
  /** Persist inj_wells + background re-simulate (called on drag end only) */
  onInjWellsCommit?: (injWells: Record<string, Record<string, number>>) => void | Promise<void>;
  /** Apply Optimize Accept (wells schedule + inj + result). */
  onOptimizeAccept?: (proposal: MbOptimizeProposal) => void | Promise<void>;
  running?: boolean;
  readOnly?: boolean;
  error?: string | null;
}

type ChartSeries = {
  id: string;
  pts: { year: number; value: number }[];
  color: string;
  dash?: string;
  /** Default left (rates). Use right for water cut %. */
  axis?: 'left' | 'right';
};

/** Round up to a clean axis ceiling so the top tick isn't max*1.08 float noise. */
function niceCeil(value: number): number {
  if (!(value > 0) || !Number.isFinite(value)) return 1;
  const exp = Math.floor(Math.log10(value));
  const base = 10 ** exp;
  const frac = value / base;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
  return niceFrac * base;
}

function formatYTick(v: number): string {
  if (!Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs >= 100 || Number.isInteger(v)) return String(Math.round(v));
  if (abs >= 10) return (Math.round(v * 10) / 10).toString();
  return (Math.round(v * 100) / 100).toString();
}

function mergeChartSeries(seriesList: ChartSeries[]): {
  data: Record<string, number>[];
  keys: { id: string; color: string; dash?: string; axis: 'left' | 'right' }[];
} {
  const years = new Set<number>();
  for (const s of seriesList) {
    for (const p of s.pts) years.add(p.year);
  }
  const sorted = [...years].sort((a, b) => a - b);
  const data = sorted.map((year) => {
    const row: Record<string, number> = { year };
    for (const s of seriesList) {
      const pt = s.pts.find((p) => p.year === year);
      row[s.id] = pt?.value ?? 0;
    }
    return row;
  });
  return {
    data,
    keys: seriesList.map((s) => ({
      id: s.id,
      color: s.color,
      dash: s.dash,
      axis: s.axis ?? 'left',
    })),
  };
}

function WellLinks({
  title,
  ids,
  kind,
  onSelect,
  height,
}: {
  title: string;
  ids: string[];
  kind: 'prod' | 'inj';
  onSelect: (engineId: string) => void;
  height?: number;
}) {
  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        height: height ?? '100%',
        maxHeight: height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5, flexShrink: 0 }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {ids.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
            No direct Voronoi links
          </Typography>
        ) : (
          <Stack spacing={0.25}>
            {ids.map((id) => (
              <Button
                key={id}
                size="small"
                variant="text"
                onClick={() => onSelect(id)}
                sx={{
                  justifyContent: 'flex-start',
                  minWidth: 0,
                  px: 0.5,
                  py: 0.15,
                  fontSize: 11,
                  textTransform: 'none',
                  color: kind === 'inj' ? 'info.main' : 'success.dark',
                }}
              >
                {id}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function RateChart({
  title,
  series,
  yLabel,
  yRightLabel,
  xDomain,
  height = 200,
  editable = false,
  onEdit,
  onEditEnd,
}: {
  title: string;
  series: ChartSeries[];
  yLabel: string;
  /** When any series uses axis: 'right' */
  yRightLabel?: string;
  xDomain: [number, number] | null;
  height?: number;
  /** Vertical drag of points (single left-axis series). Same Recharts look. */
  editable?: boolean;
  /** Live preview while dragging (do not persist / simulate here). */
  onEdit?: (pts: InjPt[]) => void;
  /** Commit once on mouseup — parent may persist + background re-simulate. */
  onEditEnd?: (pts: InjPt[]) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [showDots, setShowDots] = useState(false);
  const draggingYearRef = useRef<number | null>(null);
  const dataRef = useRef<Record<string, number>[]>([]);
  const yMaxRef = useRef(1);
  const lastPtsRef = useRef<InjPt[]>([]);
  /** Local overlay while dragging / until parent props catch up — avoids snap-back. */
  const [dragOverlay, setDragOverlay] = useState<Record<string, number>[] | null>(null);

  const { data, keys } = useMemo(() => mergeChartSeries(series), [series]);
  const hasRight = keys.some((k) => k.axis === 'right');
  const editKey = editable && keys.length === 1 && keys[0].axis === 'left' ? keys[0] : null;

  const displayData = dragOverlay ?? data;

  const yMax = useMemo(() => {
    if (!editKey) return 1;
    let m = 0;
    for (const row of displayData) m = Math.max(m, Number(row[editKey.id]) || 0);
    return niceCeil(m * 1.08 || 1);
  }, [displayData, editKey]);

  dataRef.current = displayData;
  yMaxRef.current = yMax;

  // Drop overlay once parent series matches (or selection changed upstream).
  useEffect(() => {
    if (draggingYearRef.current != null) return;
    if (!dragOverlay || !editKey) {
      if (dragOverlay && !editKey) setDragOverlay(null);
      return;
    }
    const id = editKey.id;
    const matches = dragOverlay.every((row) => {
      const parent = data.find((r) => r.year === row.year);
      if (!parent) return false;
      return Math.abs((Number(parent[id]) || 0) - (Number(row[id]) || 0)) < 0.05;
    });
    if (matches) setDragOverlay(null);
  }, [data, dragOverlay, editKey]);

  const startDrag = (year: number, e: React.MouseEvent) => {
    if (!editKey || !onEdit) return;
    e.preventDefault();
    e.stopPropagation();
    draggingYearRef.current = year;
    setShowDots(true);
    const chartEl = wrapRef.current?.querySelector('.recharts-wrapper') as HTMLElement | null;

    const applyPts = (value: number) => {
      const id = editKey.id;
      const base = dataRef.current;
      const nextRows = base.map((row) => ({
        ...row,
        [id]: row.year === year ? value : Number(row[id]) || 0,
      }));
      const pts = nextRows.map((row) => ({
        year: row.year,
        value: Number(row[id]) || 0,
      }));
      lastPtsRef.current = pts;
      setDragOverlay(nextRows);
      onEdit(pts);
      return pts;
    };

    const move = (ev: MouseEvent) => {
      if (!chartEl) return;
      const grid = chartEl.querySelector('.recharts-cartesian-grid') as SVGElement | null;
      const area = (grid || chartEl).getBoundingClientRect();
      if (area.height <= 0) return;
      const t = 1 - (ev.clientY - area.top) / area.height;
      const value = Math.max(0, t * yMaxRef.current);
      applyPts(value);
    };
    const up = () => {
      draggingYearRef.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      const pts = lastPtsRef.current;
      if (pts.length) onEditEnd?.(pts);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    move(e.nativeEvent);
  };

  if (!data.length) {
    return (
      <Box
        sx={{
          height,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          boxSizing: 'border-box',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {title} — no series yet. Run material balance.
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      ref={wrapRef}
      sx={{
        height,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 0.5,
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
      onMouseEnter={() => editable && setShowDots(true)}
      onMouseLeave={() => {
        if (draggingYearRef.current == null) setShowDots(false);
      }}
    >
      <Typography variant="caption" fontWeight={600} sx={{ px: 0.5 }}>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height="85%">
        <LineChart
          data={displayData}
          margin={{ top: 8, right: hasRight ? 8 : 12, left: 0, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
          <XAxis
            dataKey="year"
            type="number"
            domain={xDomain || ['dataMin', 'dataMax']}
            allowDataOverflow
            tick={{ fontSize: 10 }}
          />
          <YAxis
            yAxisId="left"
            type="number"
            domain={editKey ? [0, yMax] : undefined}
            tick={{ fontSize: 10 }}
            tickFormatter={formatYTick}
            width={40}
            label={{ value: yLabel, angle: -90, position: 'insideLeft', fontSize: 10 }}
          />
          {hasRight && (
            <YAxis
              yAxisId="right"
              orientation="right"
              type="number"
              domain={[0, 100]}
              tick={{ fontSize: 10 }}
              tickFormatter={formatYTick}
              width={36}
              label={
                yRightLabel
                  ? { value: yRightLabel, angle: 90, position: 'insideRight', fontSize: 10 }
                  : undefined
              }
            />
          )}
          <Tooltip
            formatter={(value, name) => {
              if (typeof value !== 'number') return [String(value ?? ''), String(name ?? '')];
              const isWc = String(name).toLowerCase().includes('water cut');
              return [isWc ? `${value.toFixed(1)}%` : value.toFixed(3), String(name ?? '')];
            }}
          />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {keys.map((k) => (
            <Line
              key={k.id}
              yAxisId={k.axis}
              type="monotone"
              dataKey={k.id}
              name={k.id}
              stroke={k.color}
              strokeDasharray={k.dash}
              strokeWidth={2}
              isAnimationActive={false}
              dot={
                editKey && k.id === editKey.id
                  ? (props: {
                      cx?: number;
                      cy?: number;
                      payload?: { year?: number };
                    }) => {
                      const { cx, cy, payload } = props;
                      if (cx == null || cy == null || payload?.year == null) return null;
                      const active = showDots || draggingYearRef.current != null;
                      if (!active) {
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={3}
                            fill={k.color}
                            fillOpacity={0}
                            style={{ cursor: 'ns-resize' }}
                            onMouseDown={(e) => startDrag(payload.year!, e)}
                          />
                        );
                      }
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={draggingYearRef.current === payload.year ? 7 : 6}
                          fill={k.color}
                          stroke="#fff"
                          strokeWidth={1.5}
                          style={{ cursor: 'ns-resize' }}
                          onMouseDown={(e) => startDrag(payload.year!, e)}
                        />
                      );
                    }
                  : false
              }
              activeDot={editKey && k.id === editKey.id ? false : undefined}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

export const MaterialBalanceProductionPanel: React.FC<MaterialBalanceProductionPanelProps> = ({
  // scenarioId, layerId, onOptimizeAccept — used when Optimize is re-enabled
  wells,
  result,
  contour = [],
  contourOrigin = null,
  so0 = 0.5,
  layerName,
  injWells = {},
  selectedWellId = null,
  onSelectWell,
  onRun,
  onInjWellsChange,
  onInjWellsCommit,
  running = false,
  readOnly = false,
  error = null,
}) => {
  const [localSel, setLocalSel] = useState<string | null>(selectedWellId);
  const [playOpen, setPlayOpen] = useState(false);
  const [playOpening, setPlayOpening] = useState(false);
  // Optimize UI parked — solver still in codebase; re-enable when ready.
  // const [optimizeOpen, setOptimizeOpen] = useState(false);
  const pendingPlayRef = useRef(false);

  useEffect(() => {
    if (!pendingPlayRef.current) return;
    if (result?.field?.length) {
      pendingPlayRef.current = false;
      setPlayOpening(false);
      setPlayOpen(true);
      return;
    }
    if (!running && playOpening) {
      pendingPlayRef.current = false;
      setPlayOpening(false);
    }
  }, [result, running, playOpening]);

  const handleOpenPlay = async () => {
    if (playOpening || running) return;
    if (result?.field?.length) {
      setPlayOpen(true);
      return;
    }
    if (!onRun || wells.length === 0) return;
    pendingPlayRef.current = true;
    setPlayOpening(true);
    try {
      await onRun();
    } catch {
      pendingPlayRef.current = false;
      setPlayOpening(false);
    }
  };

  useEffect(() => {
    setLocalSel(selectedWellId);
  }, [selectedWellId]);

  const engineByDbId = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of wells) {
      m.set(w.id, mbEngineWellId(w.well_name, w.id));
    }
    return m;
  }, [wells]);

  const dbIdByEngine = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of wells) {
      m.set(mbEngineWellId(w.well_name, w.id), w.id);
    }
    return m;
  }, [wells]);

  const selectedDbId = localSel;
  const selectedWell = wells.find((w) => w.id === selectedDbId) || null;
  const selectedEngineId = selectedWell
    ? mbEngineWellId(selectedWell.well_name, selectedWell.id)
    : null;

  const prods = wells.filter((w) => w.kind === 'prod');
  const injs = wells.filter((w) => w.kind === 'inj');

  const xDomain: [number, number] | null =
    result?.year0 != null ? [result.year0, result.year1 ?? result.year0] : null;

  const selectDb = (dbId: string) => {
    setLocalSel(dbId);
    onSelectWell?.(dbId);
  };

  const selectByEngineId = (engineId: string) => {
    const dbId = dbIdByEngine.get(engineId);
    if (dbId) selectDb(dbId);
  };

  const prodPanel = selectedWell?.kind === 'prod' && selectedEngineId;
  const injPanel = selectedWell?.kind === 'inj' && selectedEngineId;

  const prodData = prodPanel ? prodSeries(result, selectedEngineId) : null;
  const linkedInjIds = prodPanel ? linkedInjs(result, selectedEngineId) : [];
  const linkedProdIds = injPanel ? linkedProds(result, selectedEngineId) : [];
  const injPts = injPanel
    ? injSeries(result, selectedEngineId, injWells).map((p) => ({
        year: p.year,
        value: p.value,
      }))
    : [];
  const linkedRates = injPanel ? linkedProdRateSeries(result, selectedEngineId) : [];

  const buildInjWellsNext = (pts: InjPt[]): Record<string, Record<string, number>> | null => {
    if (!selectedEngineId || readOnly) return null;
    // Seed ONLY this injector from series so other injectors keep sharing residual Wi.
    const prev: Record<string, number> = { ...(injWells[selectedEngineId] || {}) };
    if (!Object.keys(prev).length) {
      for (const r of result?.series?.[selectedEngineId] || []) {
        if (r.kind === 'inj' && r.inj != null) prev[String(r.year)] = r.inj;
      }
    }
    const patch: Record<string, number> = { ...prev };
    for (const p of pts) {
      patch[String(p.year)] = Math.round(Math.max(0, p.value) * 100) / 100;
    }
    return { ...injWells, [selectedEngineId]: patch };
  };

  const handleInjPtsEdit = (pts: InjPt[]) => {
    const next = buildInjWellsNext(pts);
    if (!next) return;
    onInjWellsChange?.(next);
  };

  const handleInjPtsEditEnd = (pts: InjPt[]) => {
    const next = buildInjWellsNext(pts);
    if (!next) return;
    onInjWellsChange?.(next);
    // Persist + re-simulate in background; chart keeps overlay until props catch up.
    void onInjWellsCommit?.(next);
  };


  // Fixed chart heights + theme spacing(1)=8px gap — band height locks list bottoms to chart bottoms
  const CHART_GAP_PX = 8;
  const PROD_CHART_H = 200;
  const PROD_INJ_CHART_H = 180;
  const INJ_CHART_H = 200;
  const LINKED_CHART_H = 220;
  const bandHeight = injPanel
    ? INJ_CHART_H + LINKED_CHART_H + CHART_GAP_PX
    : prodPanel
      ? PROD_CHART_H + PROD_INJ_CHART_H + CHART_GAP_PX
      : 280;

  const wellsList = (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        height: bandHeight,
        maxHeight: bandHeight,
        minHeight: bandHeight,
        overflowY: 'auto',
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
        Wells
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ display: 'block', mb: 0.35 }}
      >
        Producers
      </Typography>
      <Stack spacing={0.15} sx={{ mb: 1 }}>
        {prods.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
        {prods.map((w) => {
          const sel = selectedDbId === w.id;
          const label = w.well_name || engineByDbId.get(w.id) || w.id;
          return (
            <Button
              key={w.id}
              size="small"
              onClick={() => selectDb(w.id)}
              fullWidth
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.75,
                px: 0.75,
                py: 0.35,
                minHeight: 0,
                borderRadius: 0.75,
                textTransform: 'none',
                border: 1,
                borderColor: sel ? 'success.main' : 'transparent',
                bgcolor: sel ? 'action.selected' : 'transparent',
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" noWrap sx={{ flex: 1, textAlign: 'left' }}>
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {w.t0}–{w.t1}
              </Typography>
            </Button>
          );
        })}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ display: 'block', mb: 0.35 }}
      >
        Injectors
      </Typography>
      <Stack spacing={0.15}>
        {injs.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
        {injs.map((w) => {
          const sel = selectedDbId === w.id;
          const label = w.well_name || engineByDbId.get(w.id) || w.id;
          return (
            <Button
              key={w.id}
              size="small"
              onClick={() => selectDb(w.id)}
              fullWidth
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.75,
                px: 0.75,
                py: 0.35,
                minHeight: 0,
                borderRadius: 0.75,
                textTransform: 'none',
                border: 1,
                borderColor: sel ? 'info.main' : 'transparent',
                bgcolor: sel ? 'action.selected' : 'transparent',
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'info.main',
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" noWrap sx={{ flex: 1, textAlign: 'left' }}>
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {w.t0}–{w.t1}
              </Typography>
            </Button>
          );
        })}
      </Stack>
    </Box>
  );

  const chartsCol = (charts: React.ReactNode) => (
    <Box
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: `${CHART_GAP_PX}px`,
        height: bandHeight,
        maxHeight: bandHeight,
        boxSizing: 'border-box',
      }}
    >
      {charts}
    </Box>
  );

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="caption" color="text.secondary">
          Voronoi LOS links · select a well for rates and injection shares
        </Typography>
        <MuiTooltip title="Play field evolution">
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={readOnly || running || playOpening || wells.length === 0}
              onClick={() => void handleOpenPlay()}
              aria-label="Play field evolution"
            >
              {running || playOpening ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <PlayIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </MuiTooltip>
      </Stack>

      <MaterialBalancePlayDialog
        open={playOpen}
        onClose={() => setPlayOpen(false)}
        result={result}
        wells={wells}
        contour={contour}
        origin={contourOrigin}
        so0={so0}
        layerName={layerName}
      />

      {/* Optimize parked — keep dialog wiring for later:
      <MaterialBalanceOptimizeDialog
        open={optimizeOpen}
        onClose={() => setOptimizeOpen(false)}
        scenarioId={scenarioId}
        layerId={layerId}
        result={result}
        wells={wells}
        injWells={injWells}
        contour={contour}
        contourOrigin={contourOrigin}
        so0={so0}
        layerName={layerName}
        readOnly={readOnly}
        onAccept={onOptimizeAccept}
      />
      */}

      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }}>
          {error}
        </Alert>
      )}

      {/* Full-width reservoir total — three Y-scales; refreshes with every simulate result */}
      <Box sx={{ width: '100%', mb: 1.5 }}>
        <FieldProductionChart
          field={result?.field}
          xDomain={xDomain}
          height={280}
          label="Reservoir total"
          extractableOilKt={
            result
              ? computeLayerExtractable({
                  initial_geological: result.initial_geological_kt,
                  recovery_factor: result.kvyt_used ?? result.target_recovery_factor ?? null,
                })
              : null
          }
          emptyHint="Reservoir total — no series yet. Run material balance."
        />
      </Box>

      {/* Header above the aligned band (wells | links | charts) */}
      {selectedWell && (
        <Box sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              {selectedWell.well_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedWell.kind === 'prod' ? 'producer' : 'injector'} · {selectedWell.t0}–
              {selectedWell.t1}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Fixed band height = sum of chart heights → bottoms align exactly */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: selectedWell ? '200px 140px minmax(0, 1fr)' : '200px minmax(0, 1fr)',
          },
          gap: 1.25,
          alignItems: 'start',
          height: { md: selectedWell ? bandHeight : 'auto' },
        }}
      >
        {wellsList}

        {!selectedWell && (
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              minHeight: 200,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Select a well to see rates and Voronoi injection shares
            </Typography>
          </Box>
        )}

        {prodPanel && selectedWell && selectedEngineId && (
          <>
            <WellLinks
              title="Injectors"
              ids={linkedInjIds}
              kind="inj"
              onSelect={selectByEngineId}
              height={bandHeight}
            />
            {chartsCol(
              <>
                <RateChart
                  title={`Production ${selectedWell.well_name}`}
                  series={[
                    { id: 'oil', pts: prodData!.oil, color: '#3dcc7a' },
                    { id: 'liquid', pts: prodData!.liq, color: '#7ab0ff' },
                    {
                      id: 'water cut',
                      pts: prodData!.waterCut,
                      color: '#94a3b8',
                      dash: '4 3',
                      axis: 'right',
                    },
                  ]}
                  yLabel="kt/yr"
                  yRightLabel="WC %"
                  xDomain={xDomain}
                  height={PROD_CHART_H}
                />
                <RateChart
                  title={`Injection to ${selectedWell.well_name} (from linked injectors)`}
                  series={[{ id: 'received', pts: prodData!.recvInj, color: '#4aa3ff' }]}
                  yLabel="10³ m³/yr"
                  xDomain={xDomain}
                  height={PROD_INJ_CHART_H}
                />
              </>,
            )}
          </>
        )}

        {injPanel && selectedWell && selectedEngineId && (
          <>
            <WellLinks
              title="Producers"
              ids={linkedProdIds}
              kind="prod"
              onSelect={selectByEngineId}
              height={bandHeight}
            />
            {chartsCol(
              <>
                <RateChart
                  title={`Injection ${selectedWell.well_name}`}
                  series={[{ id: selectedEngineId, pts: injPts, color: '#4aa3ff' }]}
                  yLabel="10³ m³/yr"
                  xDomain={xDomain}
                  height={INJ_CHART_H}
                  editable={!readOnly && !!onInjWellsChange}
                  onEdit={handleInjPtsEdit}
                  onEditEnd={handleInjPtsEditEnd}
                />
                <RateChart
                  title="Linked producer rates"
                  series={linkedRates}
                  yLabel="kt/yr"
                  xDomain={xDomain}
                  height={LINKED_CHART_H}
                />
              </>,
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
